/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';

import { VisuallyHidden, breakpoints, useTheme } from '@coursera/cds-core';

import { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import ErrorMessage from 'bundles/coursera-ui/components/extended/ErrorMessage';
import StartDateButton from 'bundles/enterprise-legacy-learner-home/components/StartDateButton';
import { useIsUpswell } from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import SponsoredByMessage from 'bundles/unified-description-page-common/components/enroll/enterprise/SponsoredByMessage';
import type { EnrollButtonSize } from 'bundles/unified-description-page-common/components/enroll/enterprise/enterpriseEnrollButtonOverrideStyles';
import buttonStyles from 'bundles/unified-description-page-common/components/enroll/enterprise/enterpriseEnrollButtonOverrideStyles';

type Props = {
  shouldShowSponsoredByMessage?: boolean;
  size?: EnrollButtonSize;
  courseId?: string;
  privateSessionStart?: number;
  onClick: () => void;
  enrollButtonRef?: React.MutableRefObject<HTMLDivElement | null>;
  trackingName: string;
  dataE2eName: string;
  customMessage?: string;
  loading?: boolean;
  error?: string;
  stylesOverride?: SerializedStyles;
};

const styles = {
  button: css`
    max-width: 100%;
  `,
  biggerButton: css`
    max-width: 100%;

    ${breakpoints.up('sm')} {
      padding-left: var(--cds-spacing-1000) !important;
      padding-right: var(--cds-spacing-1000) !important;
    }
  `,
};

const EnterpriseDisplayEnrollButton = ({
  shouldShowSponsoredByMessage,
  size,
  courseId,
  privateSessionStart,
  onClick,
  enrollButtonRef,
  trackingName,
  dataE2eName,
  customMessage,
  loading,
  error,
  stylesOverride,
}: Props) => {
  const thirdPartyOrganization = useThirdPartyOrganization();
  const isUpswell = useIsUpswell();
  const theme = useTheme();

  const shouldShowBiggerCTA = size !== 'small';

  return (
    <div css={buttonStyles.buttonWrapper(theme, size)} data-e2e="enroll-button-wrapper">
      {!error ? (
        <TrackedCdsButton
          // Do not add view event for the enroll button in the sticky header
          _refAltAsTrueRef={size !== 'small' ? enrollButtonRef : undefined}
          onClick={onClick}
          trackingName={trackingName}
          data-e2e={dataE2eName}
          css={[shouldShowBiggerCTA ? styles.biggerButton : styles.button, stylesOverride]}
          loading={loading}
        >
          {customMessage || <StartDateButton courseId={courseId} privateSessionStart={privateSessionStart} />}
        </TrackedCdsButton>
      ) : (
        <ErrorMessage error={error} />
      )}
      <VisuallyHidden component="span" className="rc-A11yScreenReaderOnly">
        <div role="alert" aria-live="assertive">
          {error}
        </div>
      </VisuallyHidden>
      {shouldShowSponsoredByMessage && thirdPartyOrganization && (
        <SponsoredByMessage thirdPartyOrgName={thirdPartyOrganization.name} isUpswell={isUpswell} />
      )}
    </div>
  );
};

export default EnterpriseDisplayEnrollButton;
